import { decodeBase64Url } from '../utils/base64url';
import { ZEALOT_TOKEN_AUDIENCE, ZEALOT_TOKEN_ISSUER } from './zealotTokenConstants.mjs';

// gloss
export const decodeTokenPayload = (token: string): Record<string, unknown> | null => {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;
    return JSON.parse(decodeBase64Url(segment));
  } catch {
    return null;
  }
};

export { ZEALOT_TOKEN_AUDIENCE, ZEALOT_TOKEN_ISSUER };

// why: the one list of issuers whose tokens are a Zealot session — the API accepts them as bearers
// why: and the portal stores them from ?token=. Any other issuer (e.g. zealot-references) is not a
// why: session and must be rejected by both sides from the same source.
export const ZEALOT_SESSION_TOKEN_ISSUERS = [ZEALOT_TOKEN_ISSUER, 'zealot'] as const;

export const isZealotSessionIssuer = (iss: unknown): iss is (typeof ZEALOT_SESSION_TOKEN_ISSUERS)[number] =>
  typeof iss === 'string' && (ZEALOT_SESSION_TOKEN_ISSUERS as readonly string[]).includes(iss);
export const ZEALOT_TOKEN_EXPIRY_SECONDS = 3600;

// gloss
export const CLAIMS_NS = 'https://joinzealot.com/claims';

// gloss
export type SpoofContext = {
  impersonatedBy: string;
  impersonatedByEmail: string;
};

// gloss
export type ZealotEmbedTokenPayload = {
  iss: typeof ZEALOT_TOKEN_ISSUER;
  aud: typeof ZEALOT_TOKEN_AUDIENCE;
  type: 'fan_embed';
  email: string;
  fanUserUuid: string;
  brandId: number;
  brandID: number;
  brandUuid: string;
  exp: number;
  iat: number;
  [k: `${typeof CLAIMS_NS}/${string}`]: string;
  sub?: string;
  impersonatedBy?: string;
  impersonatedByEmail?: string;
};

// gloss
export type CreateZealotTokenInput = {
  email: string;
  fanUserUuid: string;
  brandId: number;
  brandUuid: string;
  expirySeconds?: number;
  authMethod?: string;
  spoof?: SpoofContext;
};

// gloss
export const createZealotTokenPayload = (input: CreateZealotTokenInput): ZealotEmbedTokenPayload => {
  const now = Math.floor(Date.now() / 1000);
  const exp = now + (input.expirySeconds ?? ZEALOT_TOKEN_EXPIRY_SECONDS);

  const payload: ZealotEmbedTokenPayload = {
    iss: ZEALOT_TOKEN_ISSUER,
    aud: ZEALOT_TOKEN_AUDIENCE,
    type: 'fan_embed',
    email: input.email,
    fanUserUuid: input.fanUserUuid,
    brandId: input.brandId,
    brandID: input.brandId,
    brandUuid: input.brandUuid,
    exp,
    iat: now,
    [`${CLAIMS_NS}/email`]: input.email,
    [`${CLAIMS_NS}/brand_id`]: String(input.brandId),
    [`${CLAIMS_NS}/brand_uuid`]: input.brandUuid,
    [`${CLAIMS_NS}/fan_user_uuid`]: input.fanUserUuid,
    [`${CLAIMS_NS}/app_type`]: 'advocate',
    ...(input.authMethod ? { [`${CLAIMS_NS}/auth_method`]: input.authMethod } : {}),
  };

  if (input.spoof) {
    payload.sub = `magic|${input.brandId}|${input.email}`;
    payload.impersonatedBy = input.spoof.impersonatedBy;
    payload.impersonatedByEmail = input.spoof.impersonatedByEmail;
    payload[`${CLAIMS_NS}/auth_method`] = 'magic_link';
  }

  return payload;
};

export const isZealotEmbedToken = (payload: Record<string, unknown>): payload is ZealotEmbedTokenPayload => {
  return (
    payload.iss === ZEALOT_TOKEN_ISSUER &&
    payload.type === 'fan_embed' &&
    typeof payload.email === 'string' &&
    typeof payload.brandId === 'number' &&
    typeof payload.brandUuid === 'string'
  );
};

export type ZealotTokenClaims = {
  email: string;
  fanUserUuid: string;
  brandId: number;
  brandUuid: string;
  appType: 'advocate';
};

export const parseZealotTokenClaims = (payload: ZealotEmbedTokenPayload): ZealotTokenClaims => {
  return {
    email: payload.email,
    fanUserUuid: payload.fanUserUuid,
    brandId: payload.brandId,
    brandUuid: payload.brandUuid,
    appType: 'advocate',
  };
};
